<template>
  <div class="dropdown-wrapper"
    :readonly="props.readonly && props.readonly"
    :disabled="props.disabled && props.disabled"
  >
    <div
      :title="label"
      class="dropdown"
      tabindex="0"
      ref="dropdown"
    >
      <label v-if="label">
        {{ label }}
        <span
          v-if="props.required"
          class="text-[red] font-bold"
          >*</span
        >
      </label>

      <div class="content" :class="label === '' ? 'no-label' : ''">
        <div class="label" :style="label ? 'padding-top: 8px; padding-bottom: 0' : 'padding-top: 0; padding-bottom: 8px'">
          {{ currentValue !== '' ? currentValue : 'Selecione' }}
        </div>

        <div class="arrow">
          <i class="fas fa-chevron-down"></i>
        </div>
      </div>

      <div class="options" ref="dropdownOptions">
        <slot ref="options"></slot>
      </div>

      <select
        :name="name"
        :readonly="props.readonly && props.readonly"
        :disabled="props.disabled && props.disabled"
        :required="props.required && props.required"
        :data-value="props.value"
        ref="nativeSelect"
        @change="handleChanges($event)"
      ></select>
    </div>
  </div>
</template>

<script setup>
  import { onMounted, ref } from 'vue'

  const dropdown = ref(null)
  const dropdownOptions = ref(null)
  const nativeSelect = ref(null)

  const currentValue = ref('')

  const isClosing = ref(false)

  const props = defineProps({
    name: {
      default: '',
      type: String,
    },

    value: {
      default: '',
      type: String,
    },

    label: {
      type: String,
    },

    alignBottom: {
      default: 'false',
      type: String,
    },

    readonly: {
      type: String,
    },

    disabled: {
      type: String,
    },

    required: {
      default: false,
      type: Boolean,
    },
  })

  function handleChanges(event) {
    event.target.setAttribute('data-value', event.target.value);
  }

  onMounted(() => {
    const $selectElement = nativeSelect.value

    $selectElement.innerHTML = ''

    const $emptyOptionElement = document.createElement('option')

    $emptyOptionElement.innerHTML = 'Selecione'
    $emptyOptionElement.setAttribute('disabled', 'true')
    $emptyOptionElement.setAttribute('selected', 'true')

    $selectElement.appendChild($emptyOptionElement)

    // Create native select based on component
    dropdownOptions.value.querySelectorAll('.option').forEach($option => {
      const $optionElement = document.createElement('option')

      $optionElement.value = $option.getAttribute('data-value')
      $optionElement.innerText = $option.innerText

      $selectElement.appendChild($optionElement)
    })

    // Select the default value
    nativeSelect.value.querySelectorAll('option').forEach(option => {
      if (option.value === props.value) {
        option.selected = true
      }
    })

    currentValue.value = nativeSelect.value.options[nativeSelect.value.selectedIndex].text

    dropdown.value.addEventListener('click', e => {
      e.stopPropagation()

      const $currentDropdown = dropdown.value
      const $contentLabel = $currentDropdown.querySelector('.content .label')
      const $optionsBox = dropdownOptions.value
      const $options = $optionsBox.querySelectorAll('.option')
      const $select = $currentDropdown.querySelector('select')

      if (props.alignBottom === 'true') {
        $optionsBox.style.top = 'calc(100% + 8px)'
      }

      if (isClosing.value === false) {
        openSelect()
      }

      $options.forEach($option => {
        $option.style.color = ''

        $option.addEventListener('mouseup', e => {
          $select.value = $option.dataset.value
          $select.dispatchEvent(new Event('change'))

          $contentLabel.innerText = $option.innerText

          closeSelects()
        })
      })
    })

    function openSelect() {
      const $optionsBox = dropdownOptions.value

      $optionsBox.style.display = 'block'

      setTimeout(() => {
        $optionsBox.classList.add('show')
      })
    }

    function closeSelects() {
      const $optionsBox = document.querySelectorAll('.options')

      isClosing.value = true

      $optionsBox.forEach(item => {
        item.classList.remove('show')

        setTimeout(() => {
          item.style.display = ''

          isClosing.value = false
        }, 200)
      })
    }

    document.addEventListener('mousedown', e => {
      if (!e.target.matches('.dropdown') && !e.target.matches('.options') && !e.target.matches('.options .option')) {
        closeSelects()
      }
    })
  })
</script>

<style lang="scss">
  @import '@sass/global/functions';

  .dropdown-wrapper {
    position: relative;
    max-width: 100%;
    display: inline-block;
    margin-right: 8px;

    .dropdown {
      display: inline-block;
      position: relative;
      width: 100%;
      background-color: #fff;
      border-radius: 0.25rem;
      border: none;
      padding: size(16);
      outline: none;
      transition: 0.2s ease-in-out;
      box-shadow: 0 0 0 1px rgba(#000, 10%);
      user-select: none;
      cursor: pointer;

      &:hover,
      &:focus {
        box-shadow: 0 0px 14px -5px rgb(0 0 0 / 24%),
          0 14px 14px -13px rgb(0 0 0 / 35%);
      }

      &:focus-visible {
        box-shadow: 0 6px 15px -5px rgba(#000, 20%), 0 0 0 1px color(--primary);
      }

      select {
        display: none;
      }

      label {
        position: absolute;
        top: size(4);
        left: size(16);
        color: color(--grey-300);
        transform-origin: top left;
        transition: 0.2s ease-in-out;
        transform: scale(0.8);
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: calc(106% - 30px);
        overflow: hidden;
        pointer-events: none;
      }

      .content {
        display: flex;
        justify-content: space-between;
        align-content: flex-end;
        pointer-events: none;
        position: relative;
        width: 100%;

        &.no-label {
          .label {
            top: -8px;
          }

          .arrow {
            top: -2px;
          }
        }

        .label {
          position: relative;
          width: 100%;
          padding-bottom: 8px;
          margin-bottom: -8px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .arrow {
          display: flex;
          position: relative;
          align-items: flex-end;
          margin-left: 8px;
          color: color(--grey-300);
        }
      }

      .options {
        min-width: 100%;
        max-width: 90vw;
        width: max-content;
        position: absolute;
        background-color: #fff;
        top: 0%;
        right: 0%;
        opacity: 0;
        transform: scale(0.8);
        transform-origin: top right;
        transition: 0.2s cubic-bezier(0.41, -0.16, 0.33, 1.39);
        box-shadow: none;
        border-radius: 10px;
        display: none;
        overflow: hidden;
        cursor: auto;
        z-index: 10;

        .option {
          color: #000;
          display: block;
          font-size: 1rem;
          padding: 12px size(16);
          text-decoration: none;
          cursor: default;

          &:hover {
            background-color: rgba(#000, 15%);
          }
        }

        &.show {
          box-shadow: 5px 17px 30px -7px #0000003b;
          transform: scale(1);
          opacity: 1;
        }
      }
    }
  }

  .dropdown-wrapper[disabled] {
    pointer-events: none;

    .dropdown {
      background-color: rgba(#000, 15%);
    }
  }

  .dropdown-wrapper[readonly] {
    pointer-events: none;

    .dropdown {
      background-color: rgba(#000, 15%);
    }
  }
</style>
